'use client'

import { Check, ChevronsUpDown, PlusIcon } from 'lucide-react'
import * as React from 'react'

import { Site } from 'db/prisma'
import router from 'next/router'
import { Fragment } from 'react'
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from 'website/src/components/ui/avatar'
import { Button } from '@nextui-org/react'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandList,
    CommandSeparator,
} from 'website/src/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from 'website/src/components/ui/popover'
import { cn, createEditorLink } from 'website/src/lib/utils'
import Link from 'next/link'
import { useRootProps } from 'website/src/lib/hooks'
import { atoms } from 'website/src/lib/atoms'

export type TeamGroup = {
    label: string
    teams: {
        label: string
        value: string
    }[]
}

type PopoverTriggerProps = React.ComponentPropsWithoutRef<typeof PopoverTrigger>

type Org = { orgId: string; name?: string | null }
interface TeamSwitcherProps extends PopoverTriggerProps {
    orgs: { org: Org; sites: Partial<Site>[] }[]
    orgId: string
    siteId: string
    onSiteChange: (team: Partial<Site>) => void
}

export default function TeamSwitcher({
    className,
    orgs = [],
    orgId,
    siteId,
}: TeamSwitcherProps) {
    const { projectType, userId } = useRootProps()
    const [open, setOpen] = React.useState(false)

    const selectedOrg = orgs?.find((x) => x.org.orgId === orgId)
    const selectedSite = selectedOrg?.sites?.find((x) => x.siteId === siteId)

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    // variant='outline'
                    variant='light'
                    // size='sm'
                    role='combobox'
                    aria-expanded={open}
                    aria-label='Select a team'
                    className={cn(
                        'justify-between pl-2 h-[56px] py-1 overflow-hidden items-center max-w-full flex gap-3 shrink-0  font-semibold',
                        className,
                    )}
                >
                    <Avatar className='h-full !my-1 shrink-0 '>
                        <AvatarImage
                            // src={`https://avatar.vercel.sh/${selectedSite?.siteId}.png`}
                            className='dark:brightness-100 blur-sm'
                            alt={selectedSite?.name || ''}
                        />
                        <AvatarFallback className='w-full h-full aspect-square'>
                            {get2Letters(selectedSite?.name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className='max-w-[300px] block overflow-hidden text-start'>
                        <div className='truncate text-base'>
                            {selectedSite?.name}
                        </div>
                        <div className='truncate lowercase opacity-70 text-sm'>
                            {selectedOrg?.org.name}
                        </div>
                    </div>
                    <ChevronsUpDown className='ml-auto w-4 shrink-0 opacity-70' />
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[300px] max-w-full  p-0'>
                <Command>
                    <CommandList>
                        <CommandEmpty>No team found.</CommandEmpty>
                        {orgs?.map((org) => {
                            const hideNewProj =
                                org.org.orgId === orgId &&
                                projectType !== 'org-project'
                            return (
                                <Fragment key={org.org?.orgId}>
                                    <CommandGroup
                                        heading={
                                            <div className='text-sm'>
                                                {org.org.name}
                                            </div>
                                        }
                                    >
                                        {org.sites?.map((site) => {
                                            let href = createEditorLink({
                                                projectType:
                                                    site.siteId === siteId
                                                        ? projectType
                                                        : 'org-project',
                                                orgId: site.orgId,
                                                siteId: site.siteId,
                                            })
                                            if (site.siteId === siteId) {
                                                href = window.location.pathname
                                            }
                                            return (
                                                <Link
                                                    href={href}
                                                    key={site.siteId}
                                                >
                                                    <CommandItem
                                                        onSelect={() => {
                                                            setOpen(false)
                                                        }}
                                                        className='text-sm flex gap-1 mb-2'
                                                    >
                                                        <Avatar className='flex mr-2 h-5 w-5'>
                                                            <AvatarImage
                                                                // src={`https://avatar.vercel.sh/x${site.siteId}.png`}
                                                                className='!rounded-1 blur-sm'
                                                                alt={
                                                                    site?.name ||
                                                                    ''
                                                                }
                                                            />
                                                            <AvatarFallback className='!rounded-[2px] aspect-square'>
                                                                {get2Letters(
                                                                    site?.name,
                                                                )}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className='truncate'>
                                                            {site.name}
                                                        </div>
                                                        <Check
                                                            className={cn(
                                                                'ml-auto h-4 w-4',
                                                                siteId ===
                                                                    site.siteId
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0',
                                                            )}
                                                        />
                                                    </CommandItem>
                                                </Link>
                                            )
                                        })}
                                        {!hideNewProj && (
                                            <CommandItem
                                                className='text-xs font-semibold opacity-80'
                                                onSelect={() => {
                                                    setOpen(false)
                                                    if (!userId) {
                                                        return atoms.showSignupModal.set(
                                                            true,
                                                        )
                                                    }

                                                    router.push(
                                                        `/org/${org.org.orgId}/new-site`,
                                                    )
                                                }}
                                            >
                                                <PlusIcon className='mr-3 h-5 w-5' />
                                                New Project
                                            </CommandItem>
                                        )}
                                    </CommandGroup>
                                    <CommandSeparator />
                                </Fragment>
                            )
                        })}
                    </CommandList>
                    <CommandSeparator />
                    <CommandList>
                        <CommandGroup>
                            <CommandItem
                                className='text-xs font-semibold opacity-80'
                                onSelect={() => {
                                    setOpen(false)
                                    if (!userId) {
                                        return atoms.showSignupModal.set(true)
                                    }
                                    router.push(`/new-org`)
                                }}
                            >
                                <PlusIcon className='mr-3 h-5 w-5' />
                                New Team
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}

function get2Letters(name) {
    if (!name) return ''
    const [first, second] = name.split(' ')
    if (!second) return (first?.[0] || '') + (first?.[1] || '')
    return (first?.[0] || '') + (second?.[0] || '')
}
